import { supabase } from '../lib/supabase'
import { MarketCustomColumn, MarketCustomColumnInput, MarketCustomColumnUpdate } from '../types/database'

export class MarketCustomColumnsService {
  /**
   * Get all custom columns for a specific model
   */
  static async getByModelId(modelId: number): Promise<MarketCustomColumn[]> {
    try {
      const { data, error } = await supabase
        .from('market_custom_columns')
        .select('*')
        .eq('model_id', modelId)
        .order('column_index', { ascending: true })

      if (error) {
        throw error
      }

      return data || []
    } catch (error) {
      throw error
    }
  }

  /**
   * Get a specific custom column by model and column index
   */
  static async getByModelAndIndex(modelId: number, columnIndex: 1 | 2 | 3): Promise<MarketCustomColumn | null> {
    try {
      const { data, error } = await supabase
        .from('market_custom_columns')
        .select('*')
        .eq('model_id', modelId)
        .eq('column_index', columnIndex)
        .maybeSingle()

      if (error) {
        throw error
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Create or update a custom column
   */
  static async upsert(columnData: MarketCustomColumnInput): Promise<MarketCustomColumn> {
    try {
      // Check if column already exists
      const existing = await this.getByModelAndIndex(columnData.model_id, columnData.column_index)

      if (existing) {
        // Update existing column
        const { data, error } = await supabase
          .from('market_custom_columns')
          .update({
            column_title: columnData.column_title,
            input_type: columnData.input_type,
            options: columnData.options
          })
          .eq('id', existing.id)
          .select()
          .single()

        if (error) {
          throw error
        }

        return data
      } else {
        // Create new column
        const { data, error } = await supabase
          .from('market_custom_columns')
          .insert(columnData)
          .select()
          .single()

        if (error) {
          throw error
        }

        return data
      }
    } catch (error) {
      throw error
    }
  }

  /**
   * Update an existing custom column
   */
  static async update(id: string, updates: MarketCustomColumnUpdate): Promise<MarketCustomColumn> {
    try {
      const { data, error } = await supabase
        .from('market_custom_columns')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) {
        throw error
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Delete a custom column
   */
  static async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('market_custom_columns')
        .delete()
        .eq('id', id)

      if (error) {
        throw error
      }
    } catch (error) {
      throw error
    }
  }

  /**
   * Delete a custom column by model and index
   */
  static async deleteByModelAndIndex(modelId: number, columnIndex: 1 | 2 | 3): Promise<void> {
    try {
      const { error } = await supabase
        .from('market_custom_columns')
        .delete()
        .eq('model_id', modelId)
        .eq('column_index', columnIndex)

      if (error) {
        throw error
      }
    } catch (error) {
      throw error
    }
  }

  /**
   * Get all custom columns (for admin purposes)
   */
  static async getAll(): Promise<MarketCustomColumn[]> {
    try {
      const { data, error } = await supabase
        .from('market_custom_columns')
        .select('*')
        .order('model_id', { ascending: true })
        .order('column_index', { ascending: true })

      if (error) {
        throw error
      }

      return data || []
    } catch (error) {
      throw error
    }
  }

  /**
   * Helper function to get custom column value from override data
   */
  static getCustomColumnValue(
    overrideData: any,
    columnIndex: 1 | 2 | 3
  ): string | null {
    const fieldName = `custom_${columnIndex}_value`
    return overrideData?.[fieldName] || null
  }

  /**
   * Helper function to set custom column value in override data
   */
  static setCustomColumnValue(
    overrideData: any,
    columnIndex: 1 | 2 | 3,
    value: string | null
  ): any {
    const fieldName = `custom_${columnIndex}_value`
    return {
      ...overrideData,
      [fieldName]: value
    }
  }

  /**
   * Validate custom column configuration
   */
  static validateCustomColumn(columnData: MarketCustomColumnInput): { isValid: boolean; errors: string[] } {
    const errors: string[] = []

    // Validate column title
    if (!columnData.column_title || columnData.column_title.trim().length === 0) {
      errors.push('Column title is required')
    } else if (columnData.column_title.length > 100) {
      errors.push('Column title must be 100 characters or less')
    }

    // Validate input type
    if (!['text', 'dropdown'].includes(columnData.input_type)) {
      errors.push('Input type must be either "text" or "dropdown"')
    }

    // Validate dropdown options
    if (columnData.input_type === 'dropdown') {
      if (!columnData.options || columnData.options.length === 0) {
        errors.push('Dropdown options are required when input type is "dropdown"')
      } else if (columnData.options.some(option => !option || option.trim().length === 0)) {
        errors.push('All dropdown options must be non-empty')
      } else if (columnData.options.length > 20) {
        errors.push('Maximum 20 dropdown options allowed')
      }
    }

    // Validate column index
    if (![1, 2, 3].includes(columnData.column_index)) {
      errors.push('Column index must be 1, 2, or 3')
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  }
}
